/**
 * IOSheet - iOS Slide-Up Modal/Sheet
 * Native iOS bottom sheet with drag-to-dismiss
 */

import { ReactNode, useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';

interface IOSheetProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  snapPoints?: number[];
  className?: string;
}

export default function IOSheet({
  isOpen,
  onClose,
  title,
  children,
  snapPoints = [0.5, 0.9],
  className = '',
}: IOSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const startY = useRef(0);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handleTouchStart = (e: React.TouchEvent) => {
    startY.current = e.touches[0].clientY;
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    const currentY = e.touches[0].clientY;
    const deltaY = currentY - startY.current;
    if (deltaY > 0) {
      setDragY(deltaY);
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    if (dragY > 100) {
      onClose();
    }
    setDragY(0);
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50"
      style={{
        zIndex: 9999,
      }}
    >
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/40 au-modal-backdrop"
        style={{
          opacity: Math.max(0, 0.4 - dragY / 500),
        }}
        onClick={onClose}
      />
      
      {/* Sheet */}
      <div 
        ref={sheetRef}
        className={`
          absolute bottom-0 left-0 right-0 
          bg-[var(--glass-card-bg,#fff)] rounded-t-2xl au-modal
          max-h-[90vh] overflow-hidden
          transition-transform duration-300 ease-out
          ${className}
        `}
        style={{
          transform: `translateY(${dragY}px)`,
          borderTopLeftRadius: '20px',
          borderTopRightRadius: '20px',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Drag Handle */}
        <div className="flex justify-center py-3">
          <div className="w-36 h-5 bg-[var(--ios-light-gray)] rounded-full" />
        </div>
        
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-4 pb-4 border-b border-[#C6C6C8]">
            {title && (
              <h2 className="text-[17px] font-semibold text-[var(--ios-black)]">
                {title}
              </h2>
            )}
            {onClose && (
              <button
                onClick={onClose}
                className="absolute right-4 top-4 w-11 h-11 flex items-center justify-center active:opacity-50 rounded-full"
                style={{ minWidth: '44px', minHeight: '44px' }}
              >
                <X className="w-6 h-6 text-[var(--ios-gray)]" />
              </button>
            )}
          </div>
        )}
        
        {/* Content */}
        <div 
          className="overflow-y-auto"
          style={{
            maxHeight: 'calc(90vh - 80px)',
            paddingBottom: 'env(safe-area-inset-bottom, 20px)',
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
